import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { services, achievements, partners, testimonials } from '@/mocks/companyData';

function AnimatedCounter({ end, duration = 2000, suffix = '' }: { end: string; duration?: number; suffix?: string }) {
  const [displayValue, setDisplayValue] = useState('0');
  const ref = useRef<HTMLDivElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated.current) {
            hasAnimated.current = true;
            const numericEnd = parseFloat(end.replace(/[^0-9.]/g, ''));
            const startTime = performance.now();

            const animate = (currentTime: number) => {
              const elapsed = currentTime - startTime;
              const progress = Math.min(elapsed / duration, 1);
              const eased = 1 - Math.pow(1 - progress, 3);
              const current = numericEnd * eased;

              if (end.includes('+')) {
                setDisplayValue(Math.floor(current).toLocaleString() + '+');
              } else if (end.includes('%')) {
                setDisplayValue(current.toFixed(1) + '%');
              } else {
                setDisplayValue(Math.floor(current).toLocaleString() + suffix);
              }

              if (progress < 1) {
                requestAnimationFrame(animate);
              } else {
                setDisplayValue(end);
              }
            };
            requestAnimationFrame(animate);
          }
        });
      },
      { threshold: 0.3 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [end, duration, suffix]);

  return <div ref={ref} className="tabular-nums">{displayValue}</div>;
}

export default function Home() {
  return (
    <div className="min-h-screen">
      <Navbar transparent={true} />

      {/* Hero Section */}
      <section className="relative w-full min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20warm%20golden%20amber%20data%20visualization%20flow%20with%20geometric%20network%20nodes%20and%20connecting%20lines%20floating%20in%20dark%20space%2C%20minimalist%20Japanese%20aesthetic%2C%20soft%20gradients%2C%20elegant%20tech%20atmosphere%2C%20particle%20effects%2C%20warm%20lighting%2C%20depth%20of%20field%2C%20cinematic%20quality&width=1800&height=1200&seq=hero-bg-3d-data&orientation=landscape"
            alt="3D's Card Data データ解析"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 via-foreground-950/50 to-foreground-950/80"></div>

        <div className="relative z-10 w-full max-w-[1280px] mx-auto px-6 md:px-10 text-center">
          <div className="animate-[fadeInUp_1s_ease-out]">
            <p className="text-primary-300 text-sm md:text-base font-medium tracking-[0.2em] mb-6">
              DATA DRIVEN FUTURE
            </p>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-50 leading-tight mb-8 tracking-wide">
              データの力で、<br />
              <span className="text-primary-300">未来を描く</span>
            </h1>
            <p className="text-foreground-300 text-sm md:text-base max-w-2xl mx-auto leading-relaxed mb-12">
              3Dカードデータ解析とビッグデータ分析で、企業のデータ資産を<br className="hidden md:block" />
              最大限に活用する次世代プラットフォーム
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/services"
                className="px-8 py-3.5 bg-primary-500 text-background-50 font-medium text-sm tracking-wider rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                事業紹介を見る
              </Link>
              <Link
                to="/contact"
                className="px-8 py-3.5 border border-foreground-300/40 text-foreground-50 font-medium text-sm tracking-wider rounded-full hover:bg-foreground-50/10 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                お問い合わせ
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 animate-bounce">
          <div className="w-6 h-10 border-2 border-foreground-300/40 rounded-full flex items-start justify-center p-1.5">
            <div className="w-1 h-2.5 bg-foreground-300/60 rounded-full animate-[scrollDot_1.5s_ease-in-out_infinite]"></div>
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="relative -mt-20 z-20">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10">
          <div className="bg-background-50 rounded-2xl px-8 md:px-16 py-10 md:py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
            {achievements.map((item) => (
              <div key={item.label} className="text-center">
                <div className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-2">
                  <AnimatedCounter end={item.value} />
                </div>
                <p className="text-xs md:text-sm text-foreground-500 tracking-wide">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About preview */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 md:gap-20 items-center">
            <div>
              <span className="inline-block text-xs font-medium tracking-[0.15em] text-primary-600 bg-primary-100/70 px-4 py-1.5 rounded-full mb-6">
                ABOUT US
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-snug mb-6 tracking-wide">
                データの可能性を<br />
                信じる企業
              </h2>
              <p className="text-foreground-600 text-sm leading-relaxed mb-8">
                3D's Card Dataは、3Dカードデータの解析技術を核として、ビッグデータ分析とAI可視化ソリューションを提供するテクノロジー企業です。2019年の創業以来、独自開発のAIエンジンと高度なデータ分析基盤により、企業のデータ資産活用を支援しています。
              </p>
              <Link
                to="/company"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors group cursor-pointer whitespace-nowrap"
              >
                会社概要を見る
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
              </Link>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20Japanese%20office%20space%20in%20Roppongi%20Hills%20Tokyo%20with%20warm%20lighting%20minimalist%20design%20wooden%20accents%20large%20windows%20overlooking%20Tokyo%20skyline%20at%20sunset%20elegant%20corporate%20atmosphere%20professional%20environment%20soft%20gradients&width=800&height=600&seq=about-office-tokyo&orientation=landscape"
                  alt="3D's Card Data 東京オフィス"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-background-50 rounded-xl px-6 py-4">
                <p className="text-xs text-foreground-500 tracking-wide">本社所在地</p>
                <p className="text-sm font-medium text-foreground-800 mt-1">東京・六本木ヒルズ</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services preview */}
      <section className="py-20 md:py-28 bg-background-100">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10">
          <div className="text-center mb-16">
            <span className="inline-block text-xs font-medium tracking-[0.15em] text-primary-600 bg-primary-100/70 px-4 py-1.5 rounded-full mb-6">
              SERVICES
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-snug mb-4 tracking-wide">
              事業紹介
            </h2>
            <p className="text-foreground-500 text-sm max-w-xl mx-auto">
              3つのコアサービスでデータ活用の全領域をカバー
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {services.map((service, index) => (
              <Link
                key={service.id}
                to="/services"
                className="group bg-background-50 rounded-2xl p-8 md:p-10 hover:bg-primary-50 transition-all duration-500 cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100/70 text-primary-600 mb-6 group-hover:bg-primary-200 transition-colors duration-500">
                  <span className="font-heading text-lg font-bold">0{index + 1}</span>
                </div>
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-3 tracking-wide">
                  {service.title}
                </h3>
                <p className="text-xs text-foreground-500 mb-4 tracking-wider">{service.titleEn}</p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  {service.description}
                </p>
                <div className="space-y-2">
                  {service.features.slice(0, 3).map((feature) => (
                    <div key={feature} className="flex items-center gap-2 text-xs text-foreground-500">
                      <i className="ri-check-line text-primary-500 text-sm"></i>
                      {feature}
                    </div>
                  ))}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10">
          <div className="text-center mb-16">
            <span className="inline-block text-xs font-medium tracking-[0.15em] text-accent-600 bg-accent-100/70 px-4 py-1.5 rounded-full mb-6">
              TESTIMONIALS
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-snug mb-4 tracking-wide">
              パートナー企業の声
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {testimonials.map((item) => (
              <div key={item.name} className="bg-background-100 rounded-2xl p-8 md:p-10">
                <div className="text-primary-400 text-4xl font-serif mb-6">&ldquo;</div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-8">
                  {item.quote}
                </p>
                <div className="flex items-center gap-4 pt-6 border-t border-background-300">
                  <div className="w-10 h-10 rounded-full bg-accent-200 flex items-center justify-center text-accent-700 font-bold text-sm">
                    {item.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">{item.name}</p>
                    <p className="text-xs text-foreground-500">{item.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10">
          <p className="text-center text-xs font-medium tracking-[0.15em] text-foreground-400 mb-10">
            パートナー企業
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6">
            {partners.map((partner) => (
              <span key={partner} className="text-sm text-foreground-500 font-medium tracking-wide whitespace-nowrap">
                {partner}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20abstract%20flowing%20data%20streams%20with%20golden%20amber%20particles%20floating%20in%20dark%20space%2C%20minimalist%20Japanese%20tech%20aesthetic%2C%20soft%20gradients%2C%20elegant%20atmosphere%2C%20cinematic%20depth%20of%20field%2C%20luxury%20feel&width=1800&height=800&seq=cta-data-stream&orientation=landscape"
            alt="CTA 背景"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-foreground-950/80"></div>
        <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10 text-center">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-50 mb-4 tracking-wide">
            データ活用の未来へ
          </h2>
          <p className="text-foreground-300 text-sm mb-10 max-w-lg mx-auto">
            まずはお気軽にご相談ください。貴社のデータ活用を全力でサポートします。
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 font-medium text-sm tracking-wider rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            お問い合わせはこちら
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}